import { StudentDisabilityType } from '@k12/models';

/** Values stored in the ESA+ award level, matching the API's Awards.StudentDetail.ESAFundingLevel. */
export const EsaFundingLevel = {
  Base: 'Base',
  Higher: 'Higher'
} as const;

export type EsaFundingLevelValue = (typeof EsaFundingLevel)[keyof typeof EsaFundingLevel];

/** Disability categories that earn the higher ESA+ award. Mirrors the API's EsaFundingLevelPolicy. */
const HIGHER_AWARD_DISABILITY_TYPES: number[] = [
  StudentDisabilityType.Autism,
  StudentDisabilityType.HearingImpairment,
  StudentDisabilityType.IntellectualDisabilityModerate,
  StudentDisabilityType.IntellectualDisabilitySevere,
  StudentDisabilityType.VisualImpairmentIncludingBlindness,
  StudentDisabilityType.OrthopedicImpairment
];

/**
 * Classifies an ESA+ student's award level from their recorded disabilities. A qualifying category
 * in either the primary or the secondary slot earns the higher award; everything else is base.
 *
 * The API applies the same rule on save — this is here so the admin sees the classification update
 * as soon as a disability is picked, rather than only after the record round-trips.
 *
 * The dropdowns use '' for "None", so values arrive as number | string | null.
 */
export function resolveEsaFundingLevel(
  disabilityType1Id: unknown,
  disabilityType2Id: unknown
): EsaFundingLevelValue {
  return qualifiesForHigherAward(disabilityType1Id) || qualifiesForHigherAward(disabilityType2Id)
    ? EsaFundingLevel.Higher
    : EsaFundingLevel.Base;
}

function qualifiesForHigherAward(disabilityTypeId: unknown): boolean {
  const id = Number(disabilityTypeId);
  return disabilityTypeId !== null && disabilityTypeId !== '' && HIGHER_AWARD_DISABILITY_TYPES.includes(id);
}
